import { useEffect, useState } from 'react';

/**
 * Debounce a VALUE, not a callback.
 *
 * This distinction is the whole reason the file exists, so it is worth stating
 * plainly. The obvious way to build a typeahead is to debounce the FETCH:
 * `useEffect` + `setTimeout` + `setState(results)`. That architecture has a
 * race baked into it — request N+1 can resolve before request N, and whichever
 * lands last wins, so a fast response for "gu" can overwrite a slow one for
 * "guru" and leave the box showing results for a term the user has moved past.
 * Aborting helps but does not close it: abort is best-effort and the resolve
 * handler may already be queued.
 *
 * Debouncing the value instead moves the problem somewhere it cannot exist.
 * The debounced string goes into the react-query KEY, so each term is its own
 * cache entry and the component only ever renders the entry for the term it is
 * currently asking about. A late response for an older key is written to that
 * older key and simply never read. There is no "last write wins" because there
 * is no shared slot to win.
 *
 * Returns the previous value until `delayMs` has passed with no further change.
 */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState<T>(value);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebounced(value);
    }, delayMs);

    // Every change cancels the pending settle, which is what makes this a
    // debounce rather than a throttle: a user who keeps typing never fires.
    return () => {
      window.clearTimeout(timer);
    };
  }, [value, delayMs]);

  return debounced;
}
